/**
 * Buy Buttons Component
 * Handles Add to Cart and Buy it Now functionality
 */

export default class BuyButtons {
  constructor() {
    this.container = document;
    this.boundClickHandler = this.handleClick.bind(this);
    this.boundSubmitHandler = this.handleSubmit.bind(this);
    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // Bind events with named functions for easier cleanup
    this.container.addEventListener('click', this.boundClickHandler);
    this.container.addEventListener('submit', this.boundSubmitHandler);
  }

  handleClick(event) {
    const buyItNowButton = event.target.closest('.maybourne-buy-it-now-btn[data-redirect-to-cart="true"]');
    if (buyItNowButton) {
      event.preventDefault();
      this.handleBuyItNowClick(buyItNowButton);
    }
  }

  handleSubmit(event) {
    const form = event.target;
    if (form.action && form.action.includes('/cart/add')) {
      const submitter = event.submitter;
      if (submitter && submitter.classList.contains('maybourne-buy-it-now-btn')) {
        event.preventDefault();
        this.handleBuyItNowSubmit(form, submitter);
      }
    }
  }

  async handleBuyItNowClick(button) {
    const form = button.closest('form');
    if (!form) {
      console.error('Buy it Now: No form found');
      return;
    }

    await this.handleBuyItNowSubmit(form, button);
  }

  async handleBuyItNowSubmit(form, button) {
    // Disable button and show loading state
    const originalText = button.textContent;
    button.textContent = 'Adding...';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      // Prepare form data
      const formData = new FormData(form);
      
      // Get sections to update (for cart drawer, etc.)
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { 
        bubbles: true, 
        detail: { sections: sectionsToBundle } 
      }));
      
      formData.set('sections', sectionsToBundle.join(','));
      formData.set('sections_url', `${Shopify.routes.root}variants/${formData.get('id')}`);

      // Add to cart
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      const responseJson = await response.json();

      if (response.ok) {
        // Show success state briefly
        button.textContent = 'Added!';
        
        // Get updated cart for header count
        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();

        // Only update header cart count without triggering drawer
        // Use a custom event that doesn't trigger cart drawer opening
        document.documentElement.dispatchEvent(new CustomEvent('cart:count-update', {
          bubbles: true,
          detail: {
            cart: cartContent
          }
        }));

        // Redirect to cart page after brief delay
        setTimeout(() => {
          window.location.href = `${Shopify.routes.root}cart`;
        }, 600);

      } else {
        // Handle error
        throw new Error(responseJson.description || 'Failed to add to cart');
      }

    } catch (error) {
      console.error('Buy it Now error:', error);
      
      // Show error state
      button.textContent = 'Error - Try Again';
      
      // Dispatch error event
      form.dispatchEvent(new CustomEvent('cart:error', {
        bubbles: true,
        detail: {
          error: error.message
        }
      }));

      // Reset button after delay
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }, 2000);

      return;
    }

    // Reset button state after successful redirect
    setTimeout(() => {
      button.textContent = originalText;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }, 1000);
  }

  // Utility method to check if we should handle this form submission
  shouldHandleBuyItNow(form, submitter) {
    return submitter && 
           submitter.classList.contains('maybourne-buy-it-now-btn') &&
           form.action && 
           form.action.includes('/cart/add');
  }

  /**
   * Destroy the component and clean up event listeners
   */
  destroy() {
    this.container.removeEventListener('click', this.boundClickHandler);
    this.container.removeEventListener('submit', this.boundSubmitHandler);
  }
} 